import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  CIRCLEONE_TEAM,
  CIRCLEONE_USER,
  CIRCLEONE_USERNAME,
} from "../../constants";
import { useUpgradeModal } from "../../context/UpgradeModalContext";
import { ReactComponent as SocialMediaIcon } from "./apps.svg";
import "./HeaderContent.css";

/* ─── Original color palette ──── */
const C = {
  bg: "#ffffff",
  hover: "#f1f3f5",
  active: "#f1f3f5",
  activeText: "#6366f1",
  activeBorder: "#6366f1",
  icon: "#212529",
  iconActive: "#6366f1",
  label: "#212529",
  labelActive: "#6366f1",
  text: "#212529",
  disabled: "grey",
  flyout: "#ffffff",
  flyoutBorder: "#e5e7eb",
  flyoutHover: "#f1f3f5",
  flyoutActive: "#f1f3f5",
  flyoutActiveText: "#6366f1",
  flyoutDisabled: "#adb5bd",
};

/* ─── Flyout item ──────────────────────────────────────────────── */
type FlyoutItemProps = {
  to?: string;
  label: string;
  icon?: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

/* ─── Nested Flyout Item (opens a second panel to the right) ──── */
type NestedFlyoutItemProps = {
  label: string;
  icon?: string;
  isActive?: boolean;
  children: React.ReactNode;
  parentFlyoutLabel?: string;
};

/* ─── Third-level nested flyout ─ */
type NestedFlyoutItemL3Props = {
  label: string;
  icon?: string;
  isActive?: boolean;
  children: React.ReactNode;
};

const NestedFlyoutItemL3 = ({ label, icon, isActive, children }: NestedFlyoutItemL3Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && ref.current.contains(target)) return;
      const l3 = document.querySelector("[data-l3-flyout]");
      if (l3 && l3.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = () => {
    setOpen((p) => !p);
  };

  const style: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 16px",
    fontSize: 15,
    fontWeight: isActive || open ? 600 : 400,
    color: isActive || open ? C.flyoutActiveText : C.text,
    backgroundColor: isActive || open ? C.flyoutActive : hovered ? C.flyoutHover : "transparent",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    borderLeft: isActive || open ? `3px solid ${C.activeBorder}` : "3px solid transparent",
    boxSizing: "border-box" as const,
    justifyContent: "space-between",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={style}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {icon && (
            <i className={`bi ${icon}`} style={{ fontSize: 15, color: isActive || open ? C.iconActive : C.icon }} />
          )}
          {label}
        </span>
        <i
          className="bi bi-chevron-right"
          style={{
            fontSize: 11,
            color: isActive || open ? C.iconActive : "#888",
            transition: "transform 0.2s",
            transform: open ? "translateX(2px)" : "none",
          }}
        />
      </div>

      {open && (
        <div
          data-l3-flyout="true"
          style={{
            position: "fixed",
            left: 85 + 210 + 4 + 210 + 4,
            bottom: 20,
            minWidth: 190,
            maxWidth: 260,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 8,
            boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.2)",
            zIndex: 10000,
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            animation: "flyout-in 0.18s ease",
          }}
        >
          <div className="flyout-panel-header">{label}</div>
          {children}
        </div>
      )}
    </div>
  );
};

const NestedFlyoutItem = ({ label, icon, isActive, children, parentFlyoutLabel }: NestedFlyoutItemProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && ref.current.contains(target)) return;
      const nested = document.querySelector("[data-nested-flyout]");
      if (nested && nested.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = () => {
    setOpen((p) => !p);
  };

  const style: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 16px",
    fontSize: 15,
    fontWeight: isActive || open ? 600 : 400,
    color: isActive || open ? C.flyoutActiveText : C.text,
    backgroundColor: isActive || open ? C.flyoutActive : hovered ? C.flyoutHover : "transparent",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background-color 0.2s ease",
    borderLeft: isActive || open ? `3px solid ${C.activeBorder}` : "3px solid transparent",
    boxSizing: "border-box" as const,
    justifyContent: "space-between",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={style}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {icon && (
            <i className={`bi ${icon}`} style={{ fontSize: 15, color: isActive || open ? C.iconActive : C.icon }} />
          )}
          {label}
        </span>
        <i
          className="bi bi-chevron-right"
          style={{
            fontSize: 11,
            color: isActive || open ? C.iconActive : "#888",
            transition: "transform 0.2s",
            transform: open ? "translateX(2px)" : "none",
          }}
        />
      </div>

      {open && (
        <div
          data-nested-flyout="true"
          style={{
            position: "fixed",
            left: 85 + 210 + 4,
            bottom: 20,
            minWidth: 210,
            maxWidth: 280,
            maxHeight: "60vh",
            backgroundColor: "#ffffff",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 8,
            boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.2)",
            zIndex: 9999,
            overflowY: "auto",
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            animation: "flyout-in 0.18s ease",
            scrollbarWidth: "thin" as const,
          }}
        >
          <div className="flyout-panel-header">{label}</div>
          {children}
        </div>
      )}
    </div>
  );
};

const FlyoutItem = ({ to, label, icon, isActive, disabled, onClick }: FlyoutItemProps) => {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    const mousedownEvent = new MouseEvent('mousedown', { bubbles: true });
    document.body.dispatchEvent(mousedownEvent);
  };

  const style: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 16px",
    fontSize: 15,
    fontWeight: isActive ? 600 : 400,
    color: disabled ? C.flyoutDisabled : isActive ? C.flyoutActiveText : C.text,
    backgroundColor: isActive ? C.flyoutActive : hovered ? C.flyoutHover : "transparent",
    cursor: disabled ? "not-allowed" : "pointer",
    textDecoration: "none",
    transition: "background-color 0.2s ease",
    borderLeft: isActive ? `3px solid ${C.activeBorder}` : "3px solid transparent",
    boxSizing: "border-box" as const,
  };

  const inner = (
    <>
      {icon && (
        <i
          className={`bi ${icon}`}
          style={{
            fontSize: 15,
            opacity: disabled ? 0.5 : 1,
            color: isActive ? C.iconActive : disabled ? C.flyoutDisabled : C.icon,
          }}
        />
      )}
      <span>{label}</span>
    </>
  );

  if (disabled) {
    return (
      <div style={style} onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        {inner}
      </div>
    );
  }
  if (to) {
    return (
      <Link to={to} style={style}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        {inner}
      </Link>
    );
  }
  return (
    <div style={style} onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {inner}
    </div>
  );
};

/* ─── Flyout panel wrapper ─────────────────────────────────────── */
const FlyoutPanel = ({ children, label, top }: {
  children: React.ReactNode;
  label: string;
  top: number;
}) => (
  <div
    className="flyout-panel"
    data-flyout="true"
    data-flyout-label={label}
    style={{ top: Math.max(8, top), bottom: 20 }}
  >
    <div className="flyout-panel-header">{label}</div>
    {children}
    <div style={{ height: 6 }} />
  </div>
);

/* ─── Rail nav item ────────────────────────────────────────────── */
type NavItemProps = {
  icon: string | React.ReactNode;
  label: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  href?: string;
  external?: boolean;
  flyout?: (top: number) => React.ReactNode;
};

const NavItem = ({ icon, label, isActive, disabled, onClick, href, external, flyout }: NavItemProps) => {
  const [hovered, setHovered] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!flyoutOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && ref.current.contains(target)) return;
      const flyoutEl = document.querySelector("[data-flyout]");
      if (flyoutEl && flyoutEl.contains(target)) return;
      setFlyoutOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [flyoutOpen]);

  const handleClick = () => {
    if (flyout) {
      if (ref.current) {
        const r = ref.current.getBoundingClientRect();
        setFlyoutTop(r.top - 8);
      }
      setFlyoutOpen((p) => !p);
    } else if (onClick) {
      onClick();
    }
  };

  const itemStyle: React.CSSProperties = {
    position: "relative",
    width: 65,
    minHeight: 56,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    backgroundColor: isActive || flyoutOpen ? C.active : hovered ? C.hover : "transparent",
    transition: "background-color 0.18s ease",
    marginBottom: 2,
    textDecoration: "none",
    border: "none",
    outline: "none",
    userSelect: "none",
  };

  const iconEl = typeof icon === "string" ? (
    <i
      className={`bi ${icon}`}
      style={{
        fontSize: 20,
        color: isActive || flyoutOpen ? C.iconActive : disabled ? C.disabled : C.icon,
        lineHeight: 1,
      }}
    />
  ) : (
    <span style={{ display: "flex", alignItems: "center", opacity: disabled ? 0.4 : 1 }}>
      {icon}
    </span>
  );

  const inner = (
    <>
      {(isActive || flyoutOpen) && (
        <div style={{
          position: "absolute",
          left: -8,
          top: "50%",
          transform: "translateY(-50%)",
          width: 3,
          height: 20,
          backgroundColor: C.activeBorder,
          borderRadius: "0 3px 3px 0",
        }} />
      )}
      {iconEl}
      <span style={{
        fontSize: 10.5,
        fontWeight: isActive || flyoutOpen ? 600 : 400,
        color: isActive || flyoutOpen ? C.labelActive : disabled ? C.disabled : C.label,
        lineHeight: 1.2,
        letterSpacing: "0.01em",
        textAlign: "center",
        maxWidth: 56,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordWrap: "break-word",
      }}>
        {label}
      </span>
    </>
  );

  if (external && href) {
    return (
      <div style={{ position: "relative" }} ref={ref}>
        <a href={href} target="_blank" rel="noopener noreferrer"
          style={itemStyle as any}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}>
          {inner}
        </a>
      </div>
    );
  }

  if (href && !flyout) {
    return (
      <div style={{ position: "relative" }} ref={ref}>
        <Link to={href} style={itemStyle as any}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}>
          {inner}
        </Link>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <div style={itemStyle} onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        {inner}
      </div>
      {flyout && flyoutOpen && flyout(flyoutTop)}
    </div>
  );
};

/* ═══ MAIN COMPONENT ════════════════════════════════════════════ */
const HeaderContents = () => {
  const [permissionList, setPermissionList] = useState<any>([]);
  const [modulePermission, setModulePermission] = useState<boolean>(false);
  const [hasPhonePermission, setHasPhonePermission] = useState<boolean>(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const permissionToShow = localStorage.getItem("user-token");
  const checkTenant = localStorage.getItem(CIRCLEONE_USER);
  const checkTeam = localStorage.getItem(CIRCLEONE_TEAM);
  const checkUsername = localStorage.getItem(CIRCLEONE_USERNAME);
  const { openUpgradeModal } = useUpgradeModal();
  const isAdmin = localStorage.getItem("user_role");
  const planName = localStorage.getItem("planName") || "";
  const isFreeOrFreemium = planName === "Free" || planName === "Freemium";

  useEffect(() => {
    if (permissionToShow) {
      const parsedPermission = JSON.parse(permissionToShow);
      const teamPermissions = parsedPermission?.teams[0];

      const dynamicKey =
        Object.keys(teamPermissions).find((key) =>
          key.includes("module_permissions")
        ) || Object.keys(teamPermissions)[0];
      const dynamicKey1 =
        Object.keys(teamPermissions).find(
          (key) => key === "module_permissions"
        ) || Object.keys(teamPermissions)[0];

      if (dynamicKey1 === "module_permissions") {
        setModulePermission(true);
      } else {
        setModulePermission(false);
      }

      const permissionListValue = teamPermissions[dynamicKey] || [];
      setPermissionList(permissionListValue);

      const hasPhone = Array.isArray(permissionListValue) && permissionListValue.includes("phone:enable");
      setHasPhonePermission(hasPhone);
    }
  }, [permissionToShow]);

  const hasPermission = (permission: string) =>
    Array.isArray(permissionList) && permissionList.includes(permission);

  const restricteduser =
    checkTeam === "Botgo" && checkUsername === "vivek@gtbotgo.io";

  /* ── Flyout builders ── */

  const dashboardFlyout = (top: number) => (
    <FlyoutPanel label="Dashboard" top={top}>
      {!isFreeOrFreemium ? (
        <>
          {!restricteduser && (
            <>
              <FlyoutItem
                to="/dashboard/email-dashboard"
                label="Email" icon="bi-envelope"
                isActive={currentPath === "/dashboard/email-dashboard"} />
              <FlyoutItem
                to="/dashboard/ticket-dashboard"
                label="Ticket" icon="bi-ticket-detailed"
                isActive={currentPath === "/dashboard/ticket-dashboard"} />
            </>
          )}

          {/* Whatsapp — victorinox & mdindia disabled */}
          {/* {["victorinox", "mdindia"].includes(checkTenant) ? (
            <FlyoutItem label="Whatsapp" icon="bi-whatsapp" disabled />
          ) : (
            <FlyoutItem
              to="/dashboard/campaign-dashboard"
              label="Whatsapp" icon="bi-whatsapp"
              isActive={currentPath === "/dashboard/campaign-dashboard"} />
          )} */}

          {/* Telephony */}
          {["victorinox", "knabfinance", "mdindia", "cslfinance", "cloudtelephony"].includes(checkTenant) ? (
            <FlyoutItem label="Telephony" icon="bi-telephone" disabled />
          ) : (
            ((isAdmin === "admin" || isAdmin === "manager") && hasPhonePermission) && (
              <FlyoutItem
                to="/dashboard/rtm-dashboard"
                label="Telephony" icon="bi-telephone"
                isActive={currentPath === "/dashboard/rtm-dashboard"} />
            )
          )}

          {/* Chat Dashboard */}
          {["stage", "globtier2", "botgo", "raptee", "meta"].includes(checkTenant) ? (
            <FlyoutItem
              to="/dashboard/conversation-dashboard"
              label="Chat Dashboard" icon="bi-chat"
              isActive={currentPath === "/dashboard/conversation-dashboard"} />
          ) : (
            <FlyoutItem label="Chat Dashboard" icon="bi-chat" disabled />
          )}

          {/* Lead */}
          {!restricteduser && (
            ["victorinox", "mdindia","payswiff","payswiffibl"].includes(checkTenant) ? (
              <FlyoutItem label="Lead" icon="bi-graph-up-arrow" disabled />
            ) : (
              <FlyoutItem
                to="/dashboard/lead-dashboard"
                label="Lead" icon="bi-graph-up-arrow"
                isActive={currentPath === "/dashboard/lead-dashboard"} />
            )
          )}
        </>
      ) : (
        /* Free / Freemium dashboard */
        <>
          {!restricteduser && (
            <FlyoutItem
              to="/dashboard/lead-dashboard"
              label="Lead" icon="bi-graph-up-arrow"
              isActive={currentPath === "/dashboard/lead-dashboard"} />
          )}
          <FlyoutItem label="Email" icon="bi-envelope" disabled onClick={openUpgradeModal} />
          <FlyoutItem label="Ticket" icon="bi-ticket-detailed" disabled onClick={openUpgradeModal} />
          {checkTenant === "victorinox" ? (
            <FlyoutItem label="Whatsapp" icon="bi-whatsapp" disabled onClick={openUpgradeModal} />
          ) : (
            <FlyoutItem label="Whatsapp" icon="bi-whatsapp" disabled onClick={openUpgradeModal} />
          )}
          {!restricteduser && (
            <>
              <FlyoutItem label="Telephony" icon="bi-telephone" disabled onClick={openUpgradeModal} />
              <FlyoutItem label="Chat Dashboard" icon="bi-chat" disabled onClick={openUpgradeModal} />
              <FlyoutItem label="Social Media" icon="bi-share" disabled onClick={openUpgradeModal} />
            </>
          )}
        </>
      )}
    </FlyoutPanel>
  );

  const activityFlyout = (top: number) => (
    <FlyoutPanel label="Activity" top={top}>
      {[
        { path: "active", label: "Today", icon: "bi-sun" },
        { path: "recent", label: "Recent", icon: "bi-clock-history" },
        { path: "unassigned", label: "Unassigned", icon: "bi-person-x" },
        { path: "sl-breached", label: "SL Breached", icon: "bi-exclamation-triangle" },
      ].map((item) =>
        isFreeOrFreemium ? (
          <FlyoutItem key={item.path} label={item.label} icon={item.icon} disabled onClick={openUpgradeModal} />
        ) : (
          <FlyoutItem key={item.path} to={`/activity/${item.path}`} label={item.label} icon={item.icon}
            isActive={currentPath === `/activity/${item.path}`} />
        )
      )}
    </FlyoutPanel>
  );

  const customerFlyout = (top: number) => (
    <FlyoutPanel label="Customer" top={top}>
      <FlyoutItem
        to={isFreeOrFreemium ? undefined : "/contact/customer/contact"}
        label="Contact" icon="bi-person"
        isActive={currentPath === "/contact/customer/contact"}
        disabled={isFreeOrFreemium}
        onClick={isFreeOrFreemium ? openUpgradeModal : undefined} />
      <FlyoutItem
        to={isFreeOrFreemium ? undefined : "/contact/customer/account"}
        label="Account" icon="bi-journal-bookmark-fill"
        isActive={currentPath === "/contact/customer/account"}
        disabled={isFreeOrFreemium}
        onClick={isFreeOrFreemium ? openUpgradeModal : undefined} />
    </FlyoutPanel>
  );

  const chatFlyout = (top: number) => (
    <FlyoutPanel label="Chat" top={top}>
      {/* victorinox, mdindia, knabfinance — disabled in File 2 */}
      {["victorinox", "mdindia", "knabfinance"].includes(checkTenant) ? (
        <>
          <FlyoutItem label="Live Chat" icon="bi-chat-dots" disabled />
          <FlyoutItem label="Chat History" icon="bi-clock-history" disabled />
        </>
      ) : isFreeOrFreemium ? (
        <>
          <FlyoutItem label="Live Chat" icon="bi-chat-dots" disabled onClick={openUpgradeModal} />
          <FlyoutItem label="Chat History" icon="bi-clock-history" disabled onClick={openUpgradeModal} />
        </>
      ) : (
        <>
          <FlyoutItem
            to="/chat/chatrooms?type=Assigned"
            label="Live Chat" icon="bi-chat-dots"
            isActive={currentPath.includes("/chat/chatrooms") && new URLSearchParams(window.location.search).get("type") === "Assigned"} />
          <FlyoutItem
            to="/chat/chatrooms?type=Resolved"
            label="Chat History" icon="bi-clock-history"
            isActive={currentPath.includes("/chat/chatrooms") && new URLSearchParams(window.location.search).get("type") === "Resolved"} />
        </>
      )}
    </FlyoutPanel>
  );

  const socialFlyout = (top: number) => (
    <FlyoutPanel label="Social Media" top={top}>
      {isFreeOrFreemium ? (
        <>
          <FlyoutItem label="Youtube" icon="bi-youtube" disabled onClick={openUpgradeModal} />
          <FlyoutItem label="Instagram" icon="bi-instagram" disabled onClick={openUpgradeModal} />
          <FlyoutItem label="Post" icon="bi-megaphone" disabled onClick={openUpgradeModal} />
        </>
      ) : (
        <>
          <FlyoutItem
            to="/social-media/youtube"
            label="Youtube" icon="bi-youtube"
            isActive={currentPath === "/social-media/youtube"} />
          <FlyoutItem
            to="/social-media/instagram"
            label="Instagram" icon="bi-instagram"
            isActive={currentPath === "/social-media/instagram"} />
          <FlyoutItem
            to="/social-media/post"
            label="Post" icon="bi-megaphone"
            isActive={currentPath === "/social-media/post"} />
        </>
      )}
    </FlyoutPanel>
  );

  const reportsFlyout = (top: number) => (
    <FlyoutPanel label="Reports" top={top}>
      {(checkTenant === "mdindia" || checkTenant === "stage" || checkTenant === "victorinox" || checkTenant === "cslfinance" || checkTenant === "knabfinance" || checkTenant === "mdindiadcinvoice"  || checkTenant === "csquare"  || checkTenant === "payswiffibl" || checkTenant === "payswiffstage"  || checkTenant === "briezi" ) && (
      <FlyoutItem
        to="/reports/downloader"
        label="Exported Report"
        icon="bi-download"
        isActive={currentPath.startsWith("/reports/downloader")}
      />
      )}
      {restricteduser ? (
        <FlyoutItem
          to="/reports/campaign/reports-list"
          label="Leads" icon="bi-graph-up-arrow"
          isActive={currentPath.startsWith("/reports/campaign")} />
      ) : (
        <>
          {/* Email */}
          {isFreeOrFreemium ? (
            <FlyoutItem label="Email" icon="bi-envelope" disabled onClick={openUpgradeModal} />
          ) : (
            <FlyoutItem
              to="/reports/email/reports-list"
              label="Email" icon="bi-envelope"
              isActive={currentPath.startsWith("/reports/email")} />
          )}

          {/* Ticket */}
          {isFreeOrFreemium ? (
            <FlyoutItem label="Ticket" icon="bi-ticket-detailed" disabled onClick={openUpgradeModal} />
          ) : (
            <FlyoutItem
              to="/reports/ticket/reports-list"
              label="Ticket" icon="bi-ticket-detailed"
              isActive={currentPath.startsWith("/reports/ticket")} />
          )}

          {/* Telephony */}
          {isFreeOrFreemium ? (
            <FlyoutItem label="Telephony" icon="bi-telephone" disabled onClick={openUpgradeModal} />
          ) : ["victorinox", "mdindia", "cloudtelephony", "cslfinance"].includes(checkTenant) ? (
            <FlyoutItem label="Telephony" icon="bi-telephone" disabled />
          ) : (
            <FlyoutItem
              to="/reports/telephony/reports-list"
              label="Telephony" icon="bi-telephone"
              isActive={currentPath.startsWith("/reports/telephony")} />
          )}

          {/* Leads */}
          {planName === "Free" ? (
            <FlyoutItem label="Leads" icon="bi-graph-up-arrow" disabled onClick={openUpgradeModal} />
          ) : ["victorinox", "mdindia"].includes(checkTenant) ? (
            <FlyoutItem label="Leads" icon="bi-graph-up-arrow" disabled />
          ) : (
            <FlyoutItem
              to="/reports/campaign/reports-list"
              label="Leads" icon="bi-graph-up-arrow"
              isActive={currentPath.startsWith("/reports/campaign")} />
          )}

          {/* Chat */}
          {isFreeOrFreemium ? (
            <FlyoutItem label="Chat" icon="bi-chat" disabled onClick={openUpgradeModal} />
          ) : ["stage", "meta", "globtier2", "botgo", "raptee"].includes(checkTenant) ? (
            <FlyoutItem
              to="/reports/conversation/conversationHistory-report"
              label="Chat" icon="bi-chat"
              isActive={currentPath.startsWith("/reports/conversation")} />
          ) : (
            <FlyoutItem label="Chat" icon="bi-chat" disabled />
          )}

          {/* Social Media */}
          {isFreeOrFreemium ? (
            <FlyoutItem label="Social Media" icon="bi-share" disabled onClick={openUpgradeModal} />
          ) : ["meta", "stage", "raptee", "globtier2"].includes(checkTenant) ? (
            <FlyoutItem
              to="/reports/report-social-media/reports-list"
              label="Social Media" icon="bi-share"
              isActive={currentPath.startsWith("/reports/report-social-media")} />
          ) : (
            <FlyoutItem label="Social Media" icon="bi-share" disabled />
          )}
        </>
      )}
    </FlyoutPanel>
  );

  const settingsFlyout = (top: number) => (
    <FlyoutPanel label="Settings" top={top}>
      {/* Users — always accessible */}
      <FlyoutItem
        to="/settings/users"
        label="Users" icon="bi-people"
        isActive={currentPath === "/settings/users"} />

      {/* Customer / Contact */}
      {isFreeOrFreemium ? (
        <FlyoutItem label="Customer" icon="bi-bag-check" disabled onClick={openUpgradeModal} />
      ) : (
        <FlyoutItem
          to="/settings/customer"
          label="Customer" icon="bi-bag-check"
          isActive={currentPath === "/settings/customer"} />
      )}

      {/* General */}
      {isFreeOrFreemium ? (
        <FlyoutItem label="General" icon="bi-sliders" disabled onClick={openUpgradeModal} />
      ) : (
        <FlyoutItem
          to="/settings/general"
          label="General" icon="bi-sliders"
          isActive={currentPath === "/settings/general"} />
      )}

      {/* Ticket */}
      {isFreeOrFreemium ? (
        <FlyoutItem label="Ticket" icon="bi-ticket-detailed" disabled onClick={openUpgradeModal} />
      ) : (
        <FlyoutItem
          to="/settings/ticket"
          label="Ticket" icon="bi-ticket-detailed"
          isActive={currentPath === "/settings/ticket"} />
      )}

      {/* Email */}
      {isFreeOrFreemium ? (
        <FlyoutItem label="Email" icon="bi-envelope" disabled onClick={openUpgradeModal} />
      ) : (
        <FlyoutItem
          to="/settings/email"
          label="Email" icon="bi-envelope"
          isActive={currentPath === "/settings/email"} />
      )}

      {/* Leads — always accessible */}
      <FlyoutItem
        to="/settings/campaign"
        label="Leads" icon="bi-menu-up"
        isActive={currentPath === "/settings/campaign"} />

      {/* Telephony */}
      {isFreeOrFreemium ? (
        <FlyoutItem label="Telephony" icon="bi-telephone" disabled onClick={openUpgradeModal} />
      ) : (
        <FlyoutItem
          to="/settings/telephony/server-configuration"
          label="Telephony" icon="bi-telephone"
          isActive={currentPath.startsWith("/settings/telephony")} />
      )}

      {/* Social Media — nested flyout */}
      <NestedFlyoutItem
        label="Social Media"
        icon="bi-share"
        isActive={
          currentPath === "/settings/integration" ||
          currentPath === "/settings/youtube" ||
          currentPath === "/settings/instagram-config" ||
          currentPath === "/settings/instagram" ||
          currentPath === "/settings/youtubebot" ||
          currentPath === "/settings/website"
        }
      >
        {isFreeOrFreemium ? (
          <>
            <FlyoutItem label="WhatsApp config" icon="bi-gear" disabled onClick={openUpgradeModal} />
            <FlyoutItem label="Youtube config" icon="bi-youtube" disabled onClick={openUpgradeModal} />
            <FlyoutItem label="Instagram config" icon="bi-instagram" disabled onClick={openUpgradeModal} />
            <NestedFlyoutItemL3
              label="Bot Configuration"
              icon="bi-robot"
              isActive={false}
            >
              <FlyoutItem label="Instagram" icon="bi-instagram" disabled onClick={openUpgradeModal} />
              <FlyoutItem label="Website" icon="bi-globe" disabled onClick={openUpgradeModal} />
              <FlyoutItem label="Youtube" icon="bi-youtube" disabled onClick={openUpgradeModal} />
            </NestedFlyoutItemL3>
          </>
        ) : (
          <>
            <FlyoutItem
              to="/settings/integration"
              label="WhatsApp config" icon="bi-gear"
              isActive={currentPath === "/settings/integration"} />
            <FlyoutItem
              to="/settings/youtube"
              label="Youtube config" icon="bi-youtube"
              isActive={currentPath === "/settings/youtube"} />
            <FlyoutItem
              to="/settings/instagram-config"
              label="Instagram config" icon="bi-instagram"
              isActive={currentPath === "/settings/instagram-config"} />
            {/* Bot Configuration — third level */}
            <NestedFlyoutItemL3
              label="Bot Configuration"
              icon="bi-robot"
              isActive={
                currentPath === "/settings/instagram" ||
                currentPath === "/settings/website" ||
                currentPath === "/settings/youtubebot"
              }
            >
              <FlyoutItem
                to="/settings/instagram"
                label="Instagram" icon="bi-instagram"
                isActive={currentPath === "/settings/instagram"} />
              <FlyoutItem
                to="/settings/website"
                label="Website" icon="bi-globe"
                isActive={currentPath === "/settings/website"} />
              <FlyoutItem
                to="/settings/youtubebot"
                label="Youtube" icon="bi-youtube"
                isActive={currentPath === "/settings/youtubebot"} />
            </NestedFlyoutItemL3>
          </>
        )}
      </NestedFlyoutItem>

      {/* Chat — admin only, from File 2 uses /settings/setting-chat path */}
      {isAdmin === "admin" && (
        isFreeOrFreemium ? (
          <FlyoutItem label="Chat" icon="bi-chat-text" disabled onClick={openUpgradeModal} />
        ) : (
          <FlyoutItem
            to="/settings/setting-chat"
            label="Chat" icon="bi-chat-text"
            isActive={currentPath === "/settings/setting-chat"} />
        )
      )}

      {/* Schedule report — disabled in File 2 for paid, shown disabled for free */}
      {isFreeOrFreemium && (
        <FlyoutItem label="Schedule report" icon="bi-clock" disabled onClick={openUpgradeModal} />
      )}

      {/* Knowledge base — disabled in File 2 for paid, shown disabled for free */}
      {isFreeOrFreemium && (
        <FlyoutItem label="Knowledge base" icon="bi-check-circle" disabled onClick={openUpgradeModal} />
      )}
    </FlyoutPanel>
  );

  /* ── Rail ── */
  return (
    <div className="rail-sidebar">

      {/* Dashboard */}
      {(hasPermission("dashboard:enable") || isFreeOrFreemium) && (
        <NavItem
          icon="bi-house-door"
          label="Dashboard"
          isActive={currentPath.includes("/dashboard")}
          flyout={dashboardFlyout}
        />
      )}

      {/* Activity */}
      {!restricteduser && (
        <NavItem
          icon="bi-activity"
          label="Activity"
          isActive={currentPath.includes("/activity")}
          flyout={activityFlyout}
        />
      )}

      {/* Users */}
      {(hasPermission("users:enable") || isFreeOrFreemium) && (
        <NavItem
          icon="bi-people"
          label="Users"
          isActive={currentPath === "/users/user"}
          href="/users/user"
        />
      )}

      {/* Customer / Contact */}
      {(hasPermission("customers:enable") || isFreeOrFreemium) && (
        <NavItem
          icon="bi-person"
          label="Customer"
          isActive={currentPath.startsWith("/contact")}
          flyout={customerFlyout}
        />
      )}

      {/* Email */}
      {((!restricteduser && hasPermission("email:enable")) || isFreeOrFreemium) && (
        <NavItem
          icon="bi-envelope-arrow-down"
          label="Email"
          isActive={currentPath.startsWith("/email")}
          href={isFreeOrFreemium ? undefined : "/email/inbox"}
          disabled={isFreeOrFreemium}
          onClick={isFreeOrFreemium ? openUpgradeModal : undefined}
        />
      )}

      {/* Ticket */}
      {((!restricteduser && hasPermission("ticket:enable")) || isFreeOrFreemium) && (
        <NavItem
          icon="bi-ticket-detailed"
          label="Ticket"
          isActive={currentPath === "/ticket/lists"}
          href={isFreeOrFreemium ? undefined : "/ticket/lists"}
          disabled={isFreeOrFreemium}
          onClick={isFreeOrFreemium ? openUpgradeModal : undefined}
        />
      )}

      {/* Leads / Campaign */}
      {(hasPermission("campaigns:enable") || isFreeOrFreemium) && (
        ["victorinox", "mdindia","payswiff","payswiffibl"].includes(checkTenant) ? (
          <NavItem   icon="bi-menu-up" label="Leads" disabled />
        ) : (
          <NavItem
            icon="bi-menu-up"
            label="Leads"
            isActive={currentPath === "/campaign/list"}
            href="/campaign/list"
          />
        )
      )}

      {/* Chat — from File 2: victorinox/mdindia/knabfinance tenant disabled */}
      {((!restricteduser && hasPermission("chat:enable")) || isFreeOrFreemium) && (
        <NavItem
          icon="bi-chat"
          label="Chat"
          isActive={currentPath.includes("/chat")}
          flyout={chatFlyout}
        />
      )}

      {/* Social Media — File 2 only shows for meta/stage/raptee in paid; always show for free/flyout */}
      {(isFreeOrFreemium ||
        ["meta", "stage", "raptee"].includes(checkTenant)) && (
          <NavItem
            icon={<SocialMediaIcon style={{ width: 20, height: 20 }} />}
            label="Social Media"
            isActive={currentPath.includes("/social-media")}
            flyout={socialFlyout}
          />
        )}

      {/* Analytics */}
      {isFreeOrFreemium && (
        <NavItem
          icon="bi-bar-chart-line"
          label="Analytics"
          isActive={false}
          disabled={true}
          onClick={openUpgradeModal}
        />
      )}



      {/* Reports */}
      {(hasPermission("reports:enable") || isFreeOrFreemium) && (
        <NavItem
          icon="bi-file-earmark-bar-graph"
          label="Reports"
          isActive={currentPath.startsWith("/reports")}
          flyout={reportsFlyout}
        />
      )}

      {/* Settings */}
      {(hasPermission("settings:enable") || isFreeOrFreemium) && (
        <NavItem
          icon="bi-sliders"
          label="Settings"
          isActive={currentPath.includes("/settings")}
          flyout={settingsFlyout}
        />
      )}

    </div>
  );
};

export default HeaderContents;